<script>
  import { useWindowFocus } from '@vueuse/core'
  import { getTypeTag } from '~~/assets/js/utils'
  import { header } from '~~/store/header.js'
  import { useCategoryLabel } from '~/composables/useCategoryLabel'
  import { sendMixpanelEvent } from '~/utils/host-browser-apis'

  export default {
    directives: {
      newsTile: {
        async mounted(el) {
          await sendMixpanelEvent('NtpNewsTile_Rendered', {
            ntp_news_category: el.dataset.newsCategory,
            ntp_news_provider: el.dataset.newsProvider,
          })
        },
      },
    },
    props: {
      slideShowData: {
        type: Array,
        default: () => [],
      },
      width: {
        type: Number,
        default: 624,
      },
      height: {
        type: Number,
        default: 358,
      },
      titleWidth: {
        type: Number,
        default: 548,
      },
      titleHeight: {
        type: Number,
        default: 78,
      },
      dotsMarginTop: {
        type: Number,
        default: 318,
      },
      withNewscatcher: {
        type: Boolean,
        default: false,
      },
      adPosition: {
        type: Array,
        default: () => [1, 4],
      },
    },
    setup() {
      return {
        categoryLabel: useCategoryLabel,
      }
    },
    data() {
      return {
        slideIndex: 1,
        slideInterval: null,
        eventNumberSlide: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7],
        header,
      }
    },
    computed: {
      containerWidth() {
        return this.width + 'px'
      },
      containerHeight() {
        return this.height + 'px'
      },
      imageHeight() {
        return this.height + 'px'
      },
      imageWidth() {
        return this.width + 'px'
      },
      titleWidthProperty() {
        return this.titleWidth + 'px'
      },
      titleHeightProperty() {
        return this.titleHeight + 'px'
      },
      dotsMarginTopProperty() {
        return this.dotsMarginTop + 'px'
      },
    },
    mounted() {
      this.showSlides(this.slideIndex)

      this.slideInterval = setInterval(this.changeSlides, 4000)
    },
    beforeUnmount() {
      clearInterval(this.slideInterval)
    },
    methods: {
      sendMixpanelEvent,
      getTypeTag,
      currentSlide(n) {
        this.showSlides((this.slideIndex = n))
      },
      changeSlides() {
        const isPageFocused = useWindowFocus()

        if (isPageFocused.value) {
          const slides = this.$refs.slides

          this.slideIndex = this.slideIndex === slides.length ? 1 : this.slideIndex + 1

          this.showSlides(this.slideIndex)
        }
      },
      showSlides(n, slideshow = true) {
        if (!slideshow) {
          clearInterval(this.slideInterval)
        }

        let i
        const slides = this.$refs.slides
        const dots = this.$refs.dots

        if (n > slides.length) {
          this.slideIndex = 1
        }
        if (n < 1) {
          this.slideIndex = slides.length
        }
        for (i = 0; i < slides.length; i++) {
          slides[i].style.display = 'none'
        }
        for (i = 0; i < dots.length; i++) {
          dots[i].className = dots[i].className.replace(' active', '')
        }
        slides[this.slideIndex - 1].style.display = 'block'
        dots[this.slideIndex - 1].className += ' active'
      },
      async navigateToSummaryPage(index, n) {
        const route = useRoute()
        const adProvider = route.query.native
        const newsCategory = this.slideShowData[n - 1]?.news_category
        const articleId = this.slideShowData[n - 1]?.id
        const newsProvider = this.slideShowData[n - 1]?.Provider
        await sendMixpanelEvent('NtpNewsTile_Clicked', {
          ntp_news_provider: newsProvider,
          ntp_news_category: newsCategory,
          ntp_tile_position: this.eventNumberSlide[index],
        })
        let summaryUrl = this.withNewscatcher
          ? `/summary?category=${newsCategory}&aid=${articleId}&headerWithLogo=${header.isNewHeader}`
          : this.slideShowData[n - 1]?.TargetedUrl
        if (this.withNewscatcher && adProvider) {
          summaryUrl += `&native=${adProvider}`
        }
        const type = getTypeTag()
        if (this.withNewscatcher && type) {
          summaryUrl += `&type=${type}`
        }
        window.open(summaryUrl, '_blank')
      },
      shouldDisplayCarouselAds() {
        const route = useRoute()
        const carouselAdsRoutes = ['p']
        return carouselAdsRoutes.includes(route.name)
      },
      async sendNewsTileRenderedEvent(category, provider) {
        await sendMixpanelEvent('NtpNewsTile_Rendered', {
          ntp_news_category: category,
          ntp_news_provider: provider,
        })
      },
    },
  }
</script>

<template>
  <div>
    <div class="slideshow">
      <div
        v-for="(n, index) in 7"
        :key="index"
        ref="slides"
        role="region"
        aria-label="News Carousel"
        class="slideshow__slide"
        :class="{ 'slideshow__ad-slide': shouldDisplayCarouselAds() && adPosition.includes(index) }"
      >
        <slot
          v-if="shouldDisplayCarouselAds() && adPosition.includes(index)"
          name="adSlot"
          :index="eventNumberSlide[index]"
        />
        <a
          v-else
          v-news-tile
          :data-news-category="slideShowData[n - 1]?.news_category"
          :data-news-provider="slideShowData[n - 1]?.Provider"
          :href="
            withNewscatcher
              ? `/summary?category=${slideShowData[n - 1]?.news_category}&aid=${
                  slideShowData[n - 1]?.id
                }&headerWithLogo=${header.isNewHeader}`
              : slideShowData[n - 1]?.TargetedUrl
          "
          target="_blank"
          class="slideshow__fill-div"
          @click.prevent="navigateToSummaryPage(index, n)"
        >
          <div class="slideshow__frame">
            <span class="slideshow__news">{{ categoryLabel(slideShowData[n - 1]?.news_category) }}</span>
          </div>
          <img :src="slideShowData[n - 1]?.ThumbnailUrl" />
          <div class="slideshow__gradient"></div>
          <div class="slideshow__title">
            <span>
              {{ slideShowData[n - 1]?.Title }}
            </span>
          </div>
          <div class="slideshow__bottom">
            <div class="slideshow__favicon">
              <img style="width: 16px; height: 16px" :src="slideShowData[n - 1]?.ProvidersLogoUrl" />
            </div>
            <span class="slideshow__provider">
              {{ slideShowData[n - 1]?.Provider }}
            </span>
          </div>
        </a>
      </div>
      <div class="slideshow__dots">
        <span
          v-for="(n, index) in 7"
          ref="dots"
          :key="index"
          class="slideshow__dot"
          @click="currentSlide(n)"
        />
      </div>
      <div class="slideshow__arrow-button">
        <button class="slideshow__arrow" @click="showSlides(--slideIndex, false)">
          <img src="public/images/arrow-left.svg?url" style="width: 16px; height: 16px" />
        </button>
        <button class="slideshow__arrow" @click="showSlides(++slideIndex, false)">
          <img src="public/images/arrow-right.svg?url" style="width: 16px; height: 16px" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .slideshow {
    width: v-bind(containerWidth);
    height: v-bind(containerHeight);
    position: relative;
    display: flex;
    margin: auto;
    border-radius: 6px;
  }

  .slideshow__fill-div {
    display: block;
    height: 100%;
    width: 100%;
  }

  .slideshow__gradient {
    width: v-bind(imageWidth);
    height: v-bind(imageHeight);
    position: relative;
    z-index: 0;
    text-align: initial;
    border-radius: 6px;
    background-image: linear-gradient(
      to bottom,
      rgba(15, 18, 19, 0) 0%,
      rgba(15, 18, 19, 0.01) 12%,
      rgba(15, 18, 19, 0.03) 21%,
      rgba(15, 18, 19, 0.07) 29%,
      rgba(15, 18, 19, 0.12) 35%,
      rgba(15, 18, 19, 0.18) 40%,
      rgba(15, 18, 19, 0.25) 45%,
      rgba(15, 18, 19, 0.33) 48%,
      rgba(15, 18, 19, 0.41) 52%,
      rgba(15, 18, 19, 0.5) 55%,
      rgba(15, 18, 19, 0.59) 60%,
      rgba(15, 18, 19, 0.67) 65%,
      rgba(15, 18, 19, 0.76) 71%,
      rgba(15, 18, 19, 0.85) 79%,
      rgba(15, 18, 19, 0.93) 88%,
      #0f1213 100%
    );
  }

  .slideshow__frame {
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 0;
    right: 0;
    border-radius: 0 6px;
    z-index: 1;
  }

  .slideshow__news {
    flex-grow: 0;
    font-size: 12px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    color: #fff;
  }

  /* Hide the images by default */

  .slideshow__slide {
    display: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    border-radius: 6px;
  }

  .slideshow__title {
    position: absolute;
    width: v-bind(titleWidthProperty);
    height: 78px;
    margin: -128px 0 0 24px;
    font-size: 32px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fff;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .slideshow__slide:hover .slideshow__title {
    text-decoration: underline;
  }

  .slideshow__bottom {
    position: absolute;
    height: 16px;
    align-self: stretch;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 0;
    width: 400px;
    margin: -40px 0 0 24px;
    z-index: 1;
  }

  .slideshow__favicon {
    width: 16px;
    height: 16px;
    flex-grow: 0;
    border-radius: 2px;
  }

  .slideshow__favicon img {
    width: inherit;
    margin-left: -6px;
  }

  .slideshow__provider {
    height: 16px;
    flex-grow: 0;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    margin-left: 8px;
    color: $onelaunch-ui-shades-600;
  }

  .slideshow__dots {
    width: 112px;
    height: 16px;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin: v-bind(dotsMarginTopProperty) 0 0 -196px;
  }

  .slideshow__dot {
    cursor: pointer;
    height: 8px;
    width: 8px;
    margin: 0 4px;
    background-color: white;
    border-radius: 50%;
    opacity: 0.4;
    display: inline-block;
    transition: background-color 0.6s ease;
  }

  .active,
  .slideshow__dot:hover {
    opacity: 0.9;
  }

  .slideshow__arrow-button {
    margin: v-bind(dotsMarginTopProperty) 0 0 24px;
    width: 68px;
    height: 16px;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 12px;
    z-index: 98;
  }

  button {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    outline: inherit;
    font: inherit;
    cursor: pointer;
  }

  .slideshow__arrow {
    display: flex;
    width: 16px;
    height: 16px;
  }

  /* Fading animation */

  .fade {
    animation-name: fade;
    animation-duration: 1.5s;
  }

  @keyframes fade {
    from {
      opacity: 0.4;
    }

    to {
      opacity: 1;
    }
  }
</style>
<style lang="scss">
  .slideshow__ad-slide {
    .ad-tile {
      background-image: linear-gradient(
        to bottom,
        rgba(15, 18, 19, 0) 0%,
        rgba(15, 18, 19, 0.01) 12%,
        rgba(15, 18, 19, 0.03) 21%,
        rgba(15, 18, 19, 0.07) 29%,
        rgba(15, 18, 19, 0.12) 35%,
        rgba(15, 18, 19, 0.18) 40%,
        rgba(15, 18, 19, 0.25) 45%,
        rgba(15, 18, 19, 0.33) 48%,
        rgba(15, 18, 19, 0.41) 52%,
        rgba(15, 18, 19, 0.5) 55%,
        rgba(15, 18, 19, 0.59) 60%,
        rgba(15, 18, 19, 0.67) 65%,
        rgba(15, 18, 19, 0.76) 71%,
        rgba(15, 18, 19, 0.85) 79%,
        rgba(15, 18, 19, 0.93) 88%,
        #0f1213 100%
      );
    }
  }
</style>
